import React, { FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleProp, View, ViewStyle } from 'react-native'

import { HumanizerVisualization, IrCall } from '@ambire-common/libs/humanizer/interfaces'
import HumanizerAddress from '@common/components/HumanizerAddress'
import Text from '@common/components/Text'
import TokenOrNft from '@common/components/TokenOrNft'
import { I18N_NAMESPACE } from '@common/config/localization/i18nCore'
import useTheme from '@common/hooks/useTheme'
import spacings, { SPACING_SM, SPACING_TY } from '@common/styles/spacings'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import { translateHumanizerVisualization } from '@common/utils/humanizerTemplate'
import ImageIcon from '@web/assets/svg/ImageIcon'
import ManifestImage from '@web/components/ManifestImage'

import { COLLECTIBLE_SIZE } from '../Collectible/styles'
import ChainVisualization from './ChainVisualization/ChainVisualization'
import DeadlineItem from './DeadlineItem'
import Erc7730Visualization from './Erc7730Visualization'

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation()
}

interface Props {
  data: IrCall['fullVisualization']
  sizeMultiplierSize?: number
  textSize?: number
  chainId: bigint
  isHistory?: boolean
  testID?: string
  hasPadding?: boolean
  imageSize?: number
  hideLinks?: boolean
  style?: StyleProp<ViewStyle>
}

type ItemRenderOptions = {
  sizeMultiplierSize: number
  textSize: number
  chainId: bigint
  isHistory?: boolean
  imageSize: number
  hideLinks: boolean
  marginRight: number
  theme: ReturnType<typeof useTheme>['theme']
}

const renderText = (
  key: React.Key,
  item: HumanizerVisualization,
  content: string,
  { textSize, marginRight }: ItemRenderOptions
) => (
  <Text
    key={key}
    style={{ maxWidth: '100%', marginRight }}
    fontSize={textSize}
    weight={item.isBold || item.type === 'action' ? 'semiBold' : 'regular'}
    appearance={
      'warning' in item && item.warning
        ? 'warningText'
        : item.type === 'label'
          ? 'secondaryText'
          : item.type === 'action'
            ? 'successText'
            : 'primaryText'
    }
  >
    {content}
  </Text>
)

const renderItem = (item: HumanizerVisualization, options: ItemRenderOptions) => {
  const {
    sizeMultiplierSize,
    textSize,
    chainId,
    isHistory,
    imageSize,
    hideLinks,
    marginRight,
    theme
  } = options
  const key = item.id
  // ZKX clear signing: a call described by an ERC-7730 descriptor
  if (item.type === 'erc7730') {
    return (
      <Erc7730Visualization
        key={key}
        visualization={item}
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        PartsRenderer={HumanizedVisualization}
        sizeMultiplierSize={sizeMultiplierSize}
        textSize={textSize}
        chainId={chainId}
        isHistory={isHistory}
        imageSize={imageSize}
        hideLinks={hideLinks}
      />
    )
  }
  if (item.type === 'token') {
    return (
      <TokenOrNft
        key={key}
        sizeMultiplierSize={sizeMultiplierSize}
        value={item.value}
        address={item.address!}
        textSize={textSize}
        chainId={chainId}
        hideLinks={hideLinks}
      />
    )
  }

  if (item.type === 'address' && item.address) {
    return (
      <View key={key} style={{ marginRight }}>
        <HumanizerAddress
          fontSize={textSize}
          address={item.address}
          chainId={chainId}
          verification={item.verification}
        />
      </View>
    )
  }

  if (item.type === 'deadline' && item.value && !isHistory)
    return (
      <DeadlineItem key={key} deadline={item.value} textSize={textSize} marginRight={marginRight} />
    )
  if (item.type === 'chain' && item.chainId)
    return (
      <ChainVisualization
        chainId={item.chainId}
        key={key}
        marginRight={marginRight}
        hideLinks={hideLinks}
      />
    )

  if (item.type === 'image' && item.content) {
    return (
      <ManifestImage
        key={key}
        uri={item.content}
        containerStyle={spacings.mrSm}
        size={imageSize}
        skeletonAppearance="primaryBackground"
        fallback={() => (
          <View
            style={[
              flexbox.flex1,
              flexbox.center,
              { backgroundColor: theme.primaryBackground, width: '100%' }
            ]}
          >
            <ImageIcon
              color={theme.secondaryText}
              width={COLLECTIBLE_SIZE / 2}
              height={COLLECTIBLE_SIZE / 2}
            />
          </View>
        )}
        imageStyle={{
          borderRadius: BORDER_RADIUS_PRIMARY,
          backgroundColor: 'transparent',
          marginRight: 0
        }}
      />
    )
  }
  if (item.type === 'link' && !hideLinks) {
    return (
      <a
        onClick={stopPropagation}
        style={{ maxWidth: '100%', marginRight }}
        key={key}
        href={item.url!}
      >
        <Text fontSize={textSize} weight="semiBold" appearance="successText">
          {item.content}
        </Text>
      </a>
    )
  }
  if (item.content) return renderText(key, item, item.content, options)

  return null
}

const TRANSLATED_TEXT_ITEM: HumanizerVisualization = { type: 'label', id: 0 }

const HumanizedVisualization: FC<Props> = ({
  data = [],
  sizeMultiplierSize = 1,
  textSize = 16,
  chainId,
  isHistory,
  testID,
  hasPadding = true,
  imageSize = 36,
  hideLinks = false,
  style
}) => {
  const marginRight = SPACING_TY * sizeMultiplierSize
  const { theme } = useTheme()
  const { i18n } = useTranslation()
  const language = i18n.language
  const segments = useMemo(
    () =>
      translateHumanizerVisualization(
        data,
        { language, getResource: (lng, ns, key) => i18n.getResource(lng, ns, key) },
        I18N_NAMESPACE
      ),
    [data, i18n, language]
  )
  const options: ItemRenderOptions = {
    sizeMultiplierSize,
    textSize,
    chainId,
    isHistory,
    imageSize,
    hideLinks,
    marginRight,
    theme
  }

  return (
    <View
      testID={testID}
      style={[
        flexbox.flex1,
        flexbox.directionRow,
        flexbox.alignCenter,
        flexbox.wrap,
        {
          marginHorizontal: hasPadding ? SPACING_SM * sizeMultiplierSize : 0
        },
        style
      ]}
    >
      {segments
        ? segments.map((segment, index) => {
            if (segment.kind === 'item') return renderItem(segment.item, options)
            if (segment.kind === 'action')
              return renderText(segment.item.id, segment.item, segment.text, options)
            return renderText(`text-${index}`, TRANSLATED_TEXT_ITEM, segment.text, options)
          })
        : data.map((item) => (item ? renderItem(item, options) : null))}
    </View>
  )
}

export default memo(HumanizedVisualization)
